import { reorder } from '@ContractBuilder/modules/navigation/modules/nav-section/utils';
import { useEntityStore } from '@ContractBuilder/store';
import { ApiPatchOperation } from '@domain/schemas/api-patch-operation';
import type { UpdateContractSectionPayload } from '@domain/schemas/payload.schema';
import { patchEntity } from '@mutations/patchEntity';
import type { PatchResponse } from '@responses/common';
import { reorderLayout } from '@root/helpers';
import type { CreateResourceSectionPayload } from '@src/mutations/payloads';
import { useMutation } from '@tanstack/react-query';

interface ReorderSectionVars {
  sectionId: string;
  destinationIndex: number;
}

interface UpdateSectionVars {
  sectionId: string;
  payload: UpdateContractSectionPayload['section'];
}

export function useSectionMutations() {
  const reloadSubmission = () => useEntityStore.getState().reloadSubmission();

  const reorderSection = useMutation({
    mutationFn: ({ sectionId, destinationIndex }: ReorderSectionVars): Promise<PatchResponse> => {
      const submission = useEntityStore.getState().submission!;
      const sourceIndex = submission.sections.findIndex((s) => s.id === sectionId);
      if (sourceIndex === -1) throw new Error(`Section not found: ${sectionId}`);
      const reorderedSections = reorder(submission.sections, sourceIndex, destinationIndex);
      const newLayout = reorderLayout(submission.layout, sectionId, destinationIndex);

      useEntityStore.setState({ submission: { ...submission, sections: reorderedSections, layout: newLayout } });

      return patchEntity({
        operation: ApiPatchOperation.ReorderResourceSection,
        reorder: { destinationIndex },
        sectionId,
      });
    },
    onSuccess: reloadSubmission,
    onError: reloadSubmission,
  });

  const createSection = useMutation({
    mutationFn: (payload: CreateResourceSectionPayload) =>
      patchEntity({ operation: ApiPatchOperation.CreateResourceSection, ...payload }),
    onSuccess: reloadSubmission,
  });

  const updateSection = useMutation({
    mutationFn: ({ sectionId, payload }: UpdateSectionVars) =>
      patchEntity({ operation: ApiPatchOperation.UpdateResourceSection, sectionId, section: payload }),
    onSuccess: reloadSubmission,
  });

  const deleteSection = useMutation({
    mutationFn: (sectionId: string) => patchEntity({ operation: ApiPatchOperation.DeleteResourceSection, sectionId }),
    onSuccess: reloadSubmission,
  });

  return { reorderSection, createSection, updateSection, deleteSection };
}
